import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, inject, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { genTinyGUID, SawDust } from '@cudirect/shared-ui-library';
import { BuildingBlock, BuildingBlockControlBaseComponent } from '../building-block.types';
import { ApiResult } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.types';
import { PreciseIDQuestionResponse, PreciseIdOOWAnswersEntity } from '@workflow/ecp-workflow/dynamic-app/types/preciseIdQuestionResponse';
import { Subscription } from 'rxjs';

/** component to Verify the user identification */
@BuildingBlock('oao-identity-verification')
@Component({
  selector: 'ecp-identity-verification',
  templateUrl: './identityVerification.Component.html'
})
export class OaoIdentityVerificationComponent extends BuildingBlockControlBaseComponent implements OnInit, OnDestroy {
  protected logger = inject(SawDust).withContext({ component: 'OaoIdentityVerificationComponent' });

  /** All Question from L360 */
  public questionListResponsponse: PreciseIDQuestionResponse = {};
  /** Answers List we are preparing */
  public questionAnswerList: PreciseIdOOWAnswersEntity[] = [];
  @ViewChildren('radioGroup') radioGroups!: QueryList<ElementRef>;
  private subscriptions: Subscription = new Subscription();

  /** Storage ID */
  public APPLICATION_ID_PATH = 'api:oaoMemberInformation/accountsAndServices/applicationId';
  public PRIMARY_APPLICANT_ID_PATH = 'api:oaoMemberInformation/accountsAndServices/primaryApplicant/applicantId';
  public CO_APPLICANT_ID_PATH = 'api:oaoMemberInformation/accountsAndServices/coApplicant/applicantId';
  public ADD_CO_APPLICANT_ID_PATH = 'api:oaoMemberInformation/accountsAndServices/additionalCoApplicant/applicantId';
  public RESPON_APPLICANT_ID_PATH = 'api:oaoMemberInformation/accountsAndServices/responsibleParties4/applicantId';
  public PRIMARY_APPLICANT_PID_COMPLETED_PATH =
    'api:oaoMemberInformation/accountsAndServices/primaryApplicant/pidVerification/isVerificationCompleted';
  public CO_APPLICANT_PID_COMPLETED_PATH =
    'api:oaoMemberInformation/accountsAndServices/coApplicant/pidVerification/isVerificationCompleted';
  public ADD_CO_APPLICANT_PID_COMPLETED_PATH =
    'api:oaoMemberInformation/accountsAndServices/additionalCoApplicant/pidVerification/isVerificationCompleted';
  public RESPON_APPLICANT_PID_COMPLETED_PATH =
    'api:oaoMemberInformation/accountsAndServices/responsibleParties4/pidVerification/isVerificationCompleted';
  public MID_APP_DECISION_ENABLED_PATH = 'api:oaoMemberInformation/isMidAppDecisionEnabled';
  public identity_UserName = 'mem:identity-verification/userName';
  /** Show Empty Report Message */
  public showEmptyReportMsg = false;
  /** Applicant Name */
  public applicantName: string;

  /** isMidAppDecisionEnabled */
  public get enableMidappDecision(): boolean {
    const stateValue = this.statefulService.wrap(this.MID_APP_DECISION_ENABLED_PATH).value;
    return typeof stateValue === 'boolean' ? stateValue : false; // returning true as default value
  }
  /** On Init */
  public ngOnInit(): void {
    /** getting PID questions */
    this.getPreciseIdQuestions();
    /** Subscribing to observable for new set of question */
    this.updatingPrecisideIdQuestions();

    /** When user submit the page, focus the first invalid field */
    this.subscriptions.add(
      this.pageSubmitted$.subscribe((value) => {
        if (value) {
          this.focusFirstInvalidField();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /** Focuses on the first invalid radio group field. */
  public focusFirstInvalidField(): void {
    for (const group of this.radioGroups) {
      const radios: NodeListOf<HTMLInputElement> = group.nativeElement.closest('fieldset').querySelectorAll('input[type="radio"]');
      const isValid = Array.from(radios).some((r: HTMLInputElement) => r.checked);
      if (!isValid) {
        this.focusService.setFocus(radios[0].id);
        return;
      }
    }
  }

  /** Logic for PID answers changes */
  public preciseIdAnsersSelection(): void {
    this.statefulService.wrap('ss:oaoMemberInformation/identityVerificationQuestions').value = JSON.stringify(
      this.questionListResponsponse
    );
    this.allowNextPage();
  }

  /** logic to handle get quesions from session or from api-- this is for back and forth navigation logic */
  public getPreciseIdQuestions(): void {
    const verificationQuestions = this.statefulService.wrap('ss:oaoMemberInformation/identityVerificationQuestions').value;
    if (verificationQuestions) {
      this.questionListResponsponse = JSON.parse(verificationQuestions.toString());
      if (!this.questionListResponsponse.oowQuestion) {
        this.LoadVerificationQuestion();
      } else {
        this.setApplicantName(this.questionListResponsponse.correlationId);
      }
    } else {
      this.LoadVerificationQuestion();
    }
  }

  public LoadVerificationQuestion(): void {
    const applicationId = this.statefulService.wrap(this.APPLICATION_ID_PATH).valueString;
    const primaryApplicantId = this.statefulService.wrap(this.PRIMARY_APPLICANT_ID_PATH).valueString;
    const coApplicantId = this.statefulService.wrap(this.CO_APPLICANT_ID_PATH).valueString;
    const addCoApplicantId = this.statefulService.wrap(this.ADD_CO_APPLICANT_ID_PATH).valueString;
    const responApplicantId = this.statefulService.wrap(this.RESPON_APPLICANT_ID_PATH).valueString;

    // eslint-disable-next-line @typescript-eslint/ban-types
    const isPrimaryAppPIDCompleted = this.statefulService.wrap(this.PRIMARY_APPLICANT_PID_COMPLETED_PATH).value as boolean;
    // eslint-disable-next-line @typescript-eslint/ban-types
    const isCoAppPIDCompleted = this.statefulService.wrap(this.CO_APPLICANT_PID_COMPLETED_PATH).value as boolean;
    // eslint-disable-next-line @typescript-eslint/ban-types
    const isAddCoAppPIDCompleted = this.statefulService.wrap(this.ADD_CO_APPLICANT_PID_COMPLETED_PATH).value as boolean;
    // eslint-disable-next-line @typescript-eslint/ban-types
    const isResponAppPIDCompleted = this.statefulService.wrap(this.RESPON_APPLICANT_PID_COMPLETED_PATH).value as boolean;

    if (!isPrimaryAppPIDCompleted && primaryApplicantId) {
      this.setApplicantName(primaryApplicantId);
      this.getAllQuestions(applicationId, primaryApplicantId);
    } else if (!isCoAppPIDCompleted && coApplicantId) {
      this.setApplicantName(coApplicantId);
      this.getAllQuestions(applicationId, coApplicantId);
    } else if (!isAddCoAppPIDCompleted && addCoApplicantId) {
      this.setApplicantName(addCoApplicantId);
      this.getAllQuestions(applicationId, addCoApplicantId);
    } else if (!isResponAppPIDCompleted && responApplicantId) {
      this.setApplicantName(responApplicantId);
      this.getAllQuestions(applicationId, responApplicantId);
    } else {
      this.applicantName = '';
      this.statefulService.wrap(this.identity_UserName).value = '';
      this.statefulService.wrap('mem:show-card-spinner').value = false;
    }
  }

  /** Get Applicant Name based on ApplicantId */
  public setApplicantName(applicantId: string): void {
    const applicantType = this.getApplicantType(applicantId);
    this.applicantName = this.statefulService.wrap(
      'api:oaoMemberInformation/accountsAndServices/' + applicantType + '/firstName'
    ).valueString;
    this.statefulService.wrap(`api:oaoMemberInformation/accountsAndServices/${applicantType}/pidVerification/isPIDPageVisited`).value =
      true;
    this.statefulService.wrap(this.identity_UserName).value = this.applicantName;
  }

  /** Logic to update new set of PID qestions */
  public updatingPrecisideIdQuestions(): void {
    this.l360Service.submissionPreciseIdAnswers.subscribe((data) => {
      this.field.dirty = false;
      this.dynamicPageService.pageSubmitted = false;
      this.field.data.value = '';
      if (data) {
        this.questionListResponsponse = JSON.parse(
          this.statefulService.wrap('ss:oaoMemberInformation/identityVerificationQuestions').valueStringOnly
        );
      } else {
        this.getPreciseIdQuestions();
      }
    });
  }
  /** Calling L360 to get PID questions */
  public getAllQuestions(applicationId: string, applicantId: string): void {
    // Add UEM customEvent
    const eventPairId = genTinyGUID(24); // TODO: Replace this with cypto.randomUUID() when TS is updated
    // Retrieve the user email and application number
    const appNumber = this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationId').valueString ?? 'Unknown';
    const user = this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/email').valueString ?? 'Unknown';

    // Log to appinsights start event
    this.logAppInsightsOOWEvent(eventPairId, 'start', appNumber, user);
    this.page.actionButtonIsLoading = true;
    this.statefulService.wrap('mem:show-card-spinner').value = true;
    this.statefulService.wrap('ss:oaoMemberInformation/isEmptyPIDReport').value = false;
    this.l360Service.getPreciseIDQuestions(applicationId, applicantId).subscribe(
      (data) => {
        this.processPIDQuestionResponse(data);
        this.logAppInsightsOOWEvent(eventPairId, 'end', appNumber, user, 'success');
      },
      (err: HttpErrorResponse) => {
        this.statefulService.wrap('mem:show-card-spinner').value = false;
        this.statefulService.wrap(this.identity_UserName).value = '';
        this.page.actionButtonIsLoading = false;
        this.field.data.value = 'true';
        this.logAppInsightsOOWEvent(eventPairId, 'end', appNumber, user, 'error', err.message ?? 'Unknown error');
        this.statefulService.wrap('ss:oaoMemberInformation/isEmptyPIDReport').value = true;
        this.navigatetoNextPage();
      }
    );
  }

  /** process PID questions response to move next page when there is no response from PID including the case of 204 as well */
  private processPIDQuestionResponse(data: ApiResult<PreciseIDQuestionResponse>) {
    if (data.isSuccess) {
      this.questionListResponsponse = data ? data.data : {};
      this.statefulService.wrap('ss:oaoMemberInformation/identityVerificationQuestions').value = JSON.stringify(
        this.questionListResponsponse
      );
      const isEmptyPID = data.message.indexOf('Empty PID Report') > -1;
      if (isEmptyPID) {
        this.statefulService.wrap('ss:oaoMemberInformation/isEmptyPIDReport').value = true;
        this.field.data.value = 'true';
        this.showEmptyReportMsg = this.enableMidappDecision ? true : false;
        this.navigatetoNextPage();
      } else {
        this.showEmptyReportMsg = false;
        if (this.questionListResponsponse && this.questionListResponsponse.oowQuestion) {
          this.allowNextPage();
        }
      }
    } else {
      this.field.data.value = 'true';
      this.statefulService.wrap('ss:oaoMemberInformation/isEmptyPIDReport').value = true;
      this.navigatetoNextPage();
    }
    this.page.actionButtonIsLoading = false;
    this.statefulService.wrap('mem:show-card-spinner').value = false;
  }

  /** Navigate to the next page on API failure, Empty PID report & irrespective of the mid-app decision flag */
  private navigatetoNextPage(): void {
    this.dynamicPageService.navigateNextPage();
  }

  /** Logic to Enable disable Save and contnue button */
  public allowNextPage(): void {
    if (this.questionListResponsponse && this.questionListResponsponse.oowQuestion) {
      if (
        this.questionListResponsponse.oowQuestion.length ===
        this.questionListResponsponse.oowQuestion.filter((t) => {
          if (t.answer) {
            return t;
          }
        }).length
      ) {
        this.field.data.value = 'true';
      }
    }
  }

  public getApplicantType(applicantId: string): string {
    const primaryApplicantId = this.statefulService.wrap(this.PRIMARY_APPLICANT_ID_PATH).valueString;
    const coApplicantId = this.statefulService.wrap(this.CO_APPLICANT_ID_PATH).valueString;
    const addCoApplicantId = this.statefulService.wrap(this.ADD_CO_APPLICANT_ID_PATH).valueString;
    const responApplicantId = this.statefulService.wrap(this.RESPON_APPLICANT_ID_PATH).valueString;
    switch (applicantId.toString()) {
      case primaryApplicantId:
        return 'primaryApplicant';
      case coApplicantId:
        return 'coApplicant';
      case addCoApplicantId:
        return 'additionalCoApplicant';
      case responApplicantId:
        return 'responsibleParties4';
    }
  }

  public getDisplayUserName(path: string): string {
    const firstName = this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/' + path + '/firstName').valueString;
    return firstName;
  }

  /** To show loader on Card */
  public get showSpinner(): boolean {
    const showCard = this.statefulService.wrap('mem:show-card-spinner').value;
    return showCard === 'true' || showCard === true;
  }

  /**
   * Centralized logging method to handle both ApplicationInsights and SawDust logging
   * @param eventName The name of the event to log
   * @param eventData The data to include with the event
   */
  private logEvent(eventName: string, eventData: Record<string, unknown>): void {
    this.applicationInsightsService.logEvent(eventName, eventData);
    this.logger.logEvent(eventName, eventData);
  }

  /** TODO: this should be hoisted up to a shared module or utilities file, as its just a duplicate of the method
   * in the acconts-and-services component.
   * Logs an event in Application Insights, typically called when an account submission process either starts or ends.
   * This method is specifically meant for tracking the status and associated details of each loan submission attempt.
   *
   * @param correlationId - Unique identifier to correlate events in a single transaction.
   * @param eventState - Represents the state of the event, whether it's 'start' or 'end'.
   * @param appNumber - Unique identifier of the account which is being processed.
   * @param user - Identifier for the user triggering this event, could be username or user ID.
   * @param status - Optional parameter that captures the status of the event. It can either be 'success' or 'error'.
   * @param errorDetails - Optional parameter that captures the details of the error, if any.
   *
   * @returns This method doesn't have a return.
   */
  private logAppInsightsOOWEvent(
    correlationId: string,
    eventState: 'start' | 'end',
    appNumber: string,
    user: string,
    status: 'success' | 'error' = null,
    errorDetails: string = null
  ): void {
    const source = 'workflow'; // we also track this event in the arcDX UI for Post-App resubmit
    const domain = window.location.origin; // TODO: SHOULD BE DEFAULT IN ALL LOGS
    this.logEvent('ecp.ui.pidOOWQuestions', {
      eventState,
      correlationId,
      status,
      appNumber,
      user,
      domain,
      source,
      errorDetails
    });
  }
}
